import { useLookup } from 'hooks';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import { MeInfo, ShortBook } from 'library/types';
import { renderAutoCompleteLocaleText } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useState } from 'react';
import { ChangePasswordDialog, LoadingBackdrop } from 'components';
import { selectAllShowBooks } from 'store/features/permissions/selector';
import { Card, Button, FormLabel, TextField, Autocomplete, Grid2 as Grid, CircularProgress } from '@mui/material';

const Profile = () => {
  // other hooks
  const intl = useIntl();
  const { items: books, loading: loadingBooks, loaded: booksLoaded } = useLookup<ShortBook>(apiService.getCompanies);

  // redux hooks
  const allShowBooks = useAppSelector(selectAllShowBooks);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MeInfo | null>(null);
  const [selectedBooks, setSelectedBooks] = useState<ShortBook[]>([]);
  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);

  // async helper functions
  const getValues = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.me();

      if (response.isSuccess) {
        setData(response.data);

        let sBooks: ShortBook[] = [];

        response.data.bookIds.forEach((bookId) => {
          const book = books.find((book) => book.id === bookId);

          if (book) {
            sBooks.push(book);
          }
        });

        setSelectedBooks(sBooks);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [books]);

  // sync helper functions
  const handleCloseChangePasswordDialog = () => setOpenChangePasswordDialog(false);
  const handleClickOpenChangePasswordDialog = () => setOpenChangePasswordDialog(true);

  // useEffect hooks
  useEffect(() => {
    if (booksLoaded) {
      getValues();
    }
  }, [booksLoaded, getValues]);

  return (
    <Card>
      <Grid container direction="row" gap={2}>
        <Grid size={{ xs: 12 }}>
          <Button variant="contained" size="small" onClick={handleClickOpenChangePasswordDialog}>
            <FormattedMessage id="profile.changePassword" defaultMessage="Parola Değiştir" />
          </Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel>{<FormattedMessage id="users.nameSurname" defaultMessage="Adı / Soyadı" />}</FormLabel>
          <TextField fullWidth size="small" id="nameSurname" variant="outlined" value={data?.nameSurname || ''} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel>{<FormattedMessage id="users.email" defaultMessage="E-Posta" />}</FormLabel>
          <TextField fullWidth size="small" id="email" variant="outlined" value={data?.email || ''} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel>{<FormattedMessage id="users.otherEmail" defaultMessage="E-Posta ( Diğeri )" />}</FormLabel>
          <TextField fullWidth size="small" id="otherEmail" variant="outlined" value={data?.otherEmail || ''} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel>{<FormattedMessage id="users.role" defaultMessage="Rol" />}</FormLabel>
          <TextField fullWidth size="small" id="otherEmail" variant="outlined" value={data?.role || ''} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormLabel>{<FormattedMessage id="users.books" defaultMessage="Projeler" />}</FormLabel>
          {allShowBooks ? (
            <TextField
              fullWidth
              size="small"
              id="bookIds"
              variant="outlined"
              value={intl.formatMessage({ id: 'users.allProjectsText', defaultMessage: 'Tüm Projelerde Yetkilidir' })}
            />
          ) : (
            <Autocomplete
              readOnly
              multiple
              options={books}
              loading={loadingBooks}
              value={(() => {
                const duplicateArr = [...selectedBooks];
                const sortedData = duplicateArr.sort((a, b) => a.title.localeCompare(b.title));

                return sortedData;
              })()}
              {...renderAutoCompleteLocaleText(intl)}
              getOptionLabel={(option: ShortBook) => option.title}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  id="bookIds"
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: loadingBooks ? <CircularProgress color="inherit" size={20} /> : null,
                    },
                  }}
                />
              )}
            />
          )}
        </Grid>
      </Grid>
      <ChangePasswordDialog open={openChangePasswordDialog} handleClose={handleCloseChangePasswordDialog} />
      <LoadingBackdrop open={loading} />
    </Card>
  );
};

export default Profile;
